import { useRef, useState } from "react";
import AgentSearchSelect from "./AgentSearchSelect";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { formatPhoneNumberDashes } from "../../../utils/services";

const ClientInfoForm = ({
  formData,
  handleFormChange,
  validationErrors,
  agents = [],
  clients = [],
  onAgentCreated,
  companyKey = "",
}) => {
  const inputClassName =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-[#239D80] focus:border-[#239D80] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#239D80] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

  const emailRef = useRef(null);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [requiresNewEmail, setRequiresNewEmail] = useState(false);

  const checkExistingEmail = (email) => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setExistingCustomer(null);
      return;
    }
    setIsCheckingEmail(true);
    try {
      const match = clients.find(
        (c) => (c.email || "").toLowerCase() === trimmed.toLowerCase()
      );
      setExistingCustomer(match || null);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailChange = (value) => {
    handleFormChange("email", value);
    handleFormChange("primaryClientId", null);
    if (existingCustomer) setExistingCustomer(null);
    if (requiresNewEmail) setRequiresNewEmail(false);
    handleFormChange("requiresNewEmail", false);
  };

  const handleUseExisting = () => {
    if (!existingCustomer) return;
    handleFormChange("primaryClientId", existingCustomer.id);
    handleFormChange("firstName", existingCustomer.first_name);
    handleFormChange("lastName", existingCustomer.last_name);
    handleFormChange("phone", existingCustomer.phone);
    handleFormChange("address", existingCustomer.address || "");
    handleFormChange("city", existingCustomer.city || "");
    handleFormChange("state", existingCustomer.state || "");
    handleFormChange("zipCode", existingCustomer.zip_code || "");
    setExistingCustomer(null);
    setRequiresNewEmail(false);
    handleFormChange("requiresNewEmail", false);
  };

  return (
    <div className="p-6 py-0">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h2>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email*
        </label>
        <input
          id="email"
          ref={emailRef}
          type="email"
          value={formData.email}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={(e) => checkExistingEmail(e.target.value)}
          onInput={(e)=>    checkExistingEmail(e.target.value)}
          placeholder="john.doe@example.com"
          className={inputClassName}
        />
        {existingCustomer && (
          <p className="mt-2 my-0 px-[5px] text-xs text-[#ec2506] font-medium">Existing customer found.</p>
        )}
        {!requiresNewEmail && validationErrors.email && (
          <p className="mt-1 my-0 px-[5px] text-xs text-[#ec2506]">{validationErrors.email}</p>
        )}
        {isCheckingEmail && (
          <p className="mt-1 px-[5px] text-gray-500 my-0 text-xs">Checking for existing customer...</p>
        )}
      </div>

      {existingCustomer && (
        <div className="mt-3 rounded-md border border-[#239D80] bg-[#239D80]/5 p-3">
          <p className="text-sm text-gray-600 mb-2">
            We found a customer with this email. Review the details below and choose how you'd like to continue.
          </p>
          <div className="text-sm font-medium text-gray-900">
            {existingCustomer.first_name} {existingCustomer.last_name}
          </div>
          <div className="text-xs text-gray-600 mb-3">
            {existingCustomer.phone} · {existingCustomer.email}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              type="button"
              onClick={handleUseExisting}
              className="bg-[#239D80] hover:bg-[#239D80]/90"
            >
              Use Existing Customer
            </Button>
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={() => {
                setExistingCustomer(null);
                setRequiresNewEmail(false);
                handleFormChange("requiresNewEmail", false);
                handleFormChange("primaryClientId", null);
                handleFormChange("email", "");
                handleFormChange("firstName", "");
                handleFormChange("lastName", "");
                handleFormChange("phone", "");
                setTimeout(() => {
                  emailRef.current?.focus();
                  emailRef.current?.select();
                }, 0);
              }}
              className="border-[#239D80] text-[#239D80] hover:bg-[#239D80]/10 hover:text-[#239D80]"
            >
              Use a Different Email
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            First Name*
          </label>
          <input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => handleFormChange("firstName", e.target.value)}
            placeholder="John"
            disabled={!!formData.primaryClientId}
            className={inputClassName}
          />
          {validationErrors.firstName && (
            <p className="mt-1 px-[5px] text-[#ec2506] my-0 text-xs">{validationErrors.firstName}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name*
          </label>
          <input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => handleFormChange("lastName", e.target.value)}
            placeholder="Doe"
            disabled={!!formData.primaryClientId}
            className={inputClassName}
          />
          {validationErrors.lastName && (
            <p className="mt-1 px-[5px] my-0 text-xs text-[#ec2506]">{validationErrors.lastName}</p>
          )}
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone Number*
        </label>
        <input
          id="phone"
          type="tel"
          inputMode="numeric"
          maxLength={12}
          value={formData.phone}
          onChange={(e) => handleFormChange("phone", formatPhoneNumberDashes(e.target.value))}
          placeholder="123-456-7890"
          disabled={!!formData.primaryClientId}
          className={inputClassName}
        />
        {validationErrors.phone && (
          <p className="mt-1 px-[5px] my-0 text-xs text-[#ec2506]">{validationErrors.phone}</p>
        )}
      </div>

      {/* Agent Information Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2.5">
          {/* First Agent Card */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <AgentSearchSelect
              label="Agent (Optional)"
              value={formData.agentId}
              onChange={(agentId) => handleFormChange("agentId", agentId)}
              onAgentCreated={onAgentCreated}
              agents={agents}
              placeholder="Search or add agent..."
              companyKey={companyKey}
            />
            {validationErrors.agentId && (
              <p className="mt-1 px-[5px] text-[#ec2506] my-0 text-xs">{validationErrors.agentId}</p>
            )}

            {formData.agentId && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent Type*</label>
                <RadioGroup
                  value={formData.agentType || ""}
                  onValueChange={(value) => handleFormChange("agentType", value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="buyers" id="buyers" />
                    <Label htmlFor="buyers" className="cursor-pointer text-sm">
                      Buyer's
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sellers" id="sellers" />
                    <Label htmlFor="sellers" className="cursor-pointer text-sm">
                      Seller's
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>

          {/* Second Agent Card */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <AgentSearchSelect
              label="Second Agent (Optional)"
              value={formData.secondAgentId}
              onChange={(agentId) => handleFormChange("secondAgentId", agentId)}
              onAgentCreated={onAgentCreated}
              agents={agents}
              placeholder="Search or add agent..."
              companyKey={companyKey}
            />

            {formData.secondAgentId && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent Type*</label>
                <RadioGroup
                  value={formData.secondAgentType || ""}
                  onValueChange={(value) => handleFormChange("secondAgentType", value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="buyers" id="second-buyers" />
                    <Label htmlFor="second-buyers" className="cursor-pointer text-sm">
                      Buyer's
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sellers" id="second-sellers" />
                    <Label htmlFor="second-sellers" className="cursor-pointer text-sm">
                      Seller's
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientInfoForm;
